import { BEAD_SPECS } from '../domain/beads';
import { mixHex } from '../domain/color';
import { EMPTY } from '../domain/types';
import type { Pattern } from '../domain/types';

/**
 * Preview of a bead crochet rope.
 *
 * The pattern's width is the circumference in beads and each round spirals
 * sideways by `ropeShift` beads, which is what produces the diagonal barber-pole
 * lines that bead crochet is known for. Beads are drawn back-to-front so the
 * far side of the tube is correctly hidden.
 */
export function renderRope(
  ctx: CanvasRenderingContext2D,
  pattern: Pattern,
  width: number,
  height: number,
  background: string,
): void {
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  const n = pattern.width;
  if (n < 3 || pattern.height < 1) return;

  const spec = BEAD_SPECS[pattern.beadSize];
  const shift = pattern.ropeShift ?? 1;

  // Size the tube from its diameter so it fills the width, then show as many
  // rounds as fit underneath. Capped so a narrow rope does not become giant.
  const circumferenceMm = n * spec.dia;
  const radiusMm = circumferenceMm / (2 * Math.PI);
  const scale = Math.min(20, (width * 0.62) / (radiusMm * 2));
  const rowsVisible = Math.min(
    pattern.height,
    Math.max(4, Math.floor((height * 0.94) / (spec.len * scale * 0.92))),
  );

  // Start at the first round that has beads. Leading empty rounds are real but
  // showing a blank tube tells the user nothing.
  let firstRound = 0;
  for (let i = 0; i < pattern.height; i++) {
    const row = pattern.height - 1 - i;
    let any = false;
    for (let col = 0; col < n; col++) {
      if (pattern.cells[row * pattern.width + col] !== EMPTY) {
        any = true;
        break;
      }
    }
    if (any) {
      firstRound = i;
      break;
    }
  }

  const R = radiusMm * scale;
  const beadW = (spec.dia * scale) / 1.05;
  const beadH = (spec.len * scale) / 1.02;
  const cx = width / 2;
  const baseY = height - beadH;

  type Item = { x: number; y: number; depth: number; hex: string };
  const items: Item[] = [];

  for (let i = 0; i < rowsVisible; i++) {
    const round = firstRound + i;
    const row = pattern.height - 1 - round; // bottom of the chart is the start
    if (row < 0) break;
    for (let col = 0; col < n; col++) {
      const v = pattern.cells[row * pattern.width + col];
      if (v === EMPTY) continue;
      const theta = (2 * Math.PI * (col + round * shift)) / n;
      const depth = Math.cos(theta);
      items.push({
        x: cx + Math.sin(theta) * R,
        y: baseY - i * beadH * 0.92 - (depth * beadH) / 6,
        depth,
        hex: pattern.palette[v]?.hex ?? '#888888',
      });
    }
  }

  items.sort((a, b) => a.depth - b.depth);

  for (const it of items) {
    // A bead at angle theta projects to cos(theta) of its width, which is what
    // makes the tube read as round rather than as a flat strip.
    if (it.depth <= 0.04) continue;
    const w = beadW * it.depth;
    if (w < 0.7) continue;
    const shade = mixHex(it.hex, '#000000', 0.45 * (1 - it.depth));
    const grad = ctx.createLinearGradient(it.x, it.y - beadH / 2, it.x, it.y + beadH / 2);
    grad.addColorStop(0, mixHex(shade, '#ffffff', 0.28));
    grad.addColorStop(0.5, shade);
    grad.addColorStop(1, mixHex(shade, '#000000', 0.25));
    ctx.beginPath();
    ctx.roundRect(it.x - w / 2, it.y - beadH / 2, w, beadH, Math.min(w, beadH) * 0.35);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 0.6;
    ctx.stroke();
  }
}
